import { FlexLayout, Typography } from '@eduact/ed-system'
import Spacer from '@eduact/ed-components/Spacer'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { QuestionContentWrapper } from './QuestionStyled'
import { entryMatchesOption, optionEntries } from './matching'

type McqAnswerValue = { answer: string; option_id?: number | null } | null | undefined

type Props = {
	question: any
	onChange: (answer: any) => void
}

/**
 * Vendored from ed-components 0.4.10 Test/McqQuestion. Same rendering; the
 * radio identity moved from the option text to the option id (text fallback
 * for legacy payloads) — see VENDORED.md.
 */
const McqQuestion: React.FC<Props> = ({ question, onChange }) => {
	const { t } = useTranslation(['test'])
	const entries = optionEntries(question)
	const current = question.answer as McqAnswerValue
	// A stored answer whose option no longer renders (edited or deleted since
	// it was saved): nothing pre-selects, so tell the student instead of
	// letting the question silently look answered-but-empty.
	const orphaned = !!current && entries.length > 0 && !entries.some(entry => entryMatchesOption(current, entry))

	const select = (entry: { id: number | null; choice: string }) => {
		// The emitted text is what this client was served; with an id present
		// the server resolves by id and snapshots ITS canonical text.
		if (entry.id != null) onChange({ answer: entry.choice, option_id: entry.id })
		else onChange({ answer: entry.choice })
	}

	return (
		<Spacer p={{ sm: '1rem' }}>
			<QuestionContentWrapper dangerouslySetInnerHTML={{ __html: question.content }} />
			<Spacer mb={{ sm: '0.813rem', lg: '2.313rem' }} />
			<FlexLayout px={{ sm: '0.813rem' }} flexDirection="column">
				<AnswersLabel fontSize={{ sm: '0.5rem', lg: '1rem' }} fontFamily="Roboto">
					Answers
				</AnswersLabel>
				<Spacer mb={{ sm: '6px', lg: '1rem' }} />
				<FlexLayout flexDirection={'column'}>
					{entries.map((entry, index) => (
						<FlexLayout alignItems={'center'} key={`${entry.id ?? entry.choice}-${index}`} mb={{ sm: '0.75rem' }}>
							<StyledRadioButton
								onChange={() => select(entry)}
								type={'radio'}
								id={`${entry.id ?? entry.choice}`}
								checked={!!current && entryMatchesOption(current, entry)}
								value={entry.choice}
								name={`answer-${question.id}`}
							/>
							<Spacer mx={{ sm: '4px' }} />
							<Typography cursor={'pointer'} onClick={() => select(entry)} fontSize={{ sm: '0.75rem', lg: '1.125rem' }}>
								<span>{entry.choice}</span>
							</Typography>
						</FlexLayout>
					))}
					{orphaned && (
						<Typography fontSize={{ sm: '0.75rem', lg: '1.125rem' }}>
							<span>
								{current?.answer} — {t('option_no_longer_exists')}
							</span>
						</Typography>
					)}
				</FlexLayout>
			</FlexLayout>
		</Spacer>
	)
}

export default McqQuestion

const StyledRadioButton = styled.input`
	width: 1rem;
	height: 1rem;
	${({ theme }) => `${theme.mediaQueries.large}{
		width :1.25rem;
		height :1.25rem;
	}`}
`
const AnswersLabel = styled(Typography)`
	color: ${props => props.theme.colors.silver};
`
